import * as React from 'react';
import { NavLink, Outlet } from 'react-router';
import { cn } from '../lib/cn';
import { tabPatterns } from '../design-system/patterns/tabs';
import { focusUtils } from '../design-system/utils/a11y';

export interface UniversalTabItem {
  label: string;
  /** Relative or absolute route path for this tab. */
  to: string;
  /** Use `end` matching for the index tab so siblings don't false-match. */
  end?: boolean;
  /** Optional icon — Lucide component. */
  icon?: React.ComponentType<{ className?: string }>;
  /** Optional badge (e.g. count). */
  badge?: string | number;
  /** Disable this tab. */
  disabled?: boolean;
}

export interface UniversalTabsProps {
  items: UniversalTabItem[];
  /** Right-side actions shown beside the tab list (e.g. "Edit", "Print"). */
  actions?: React.ReactNode;
  /** Visual style — 'underline' (default) or 'pill'. */
  variant?: 'underline' | 'pill';
  /** When set, renders the matched route inside via `<Outlet />`.
   *  Default true — pass `false` if the parent already renders the outlet. */
  withOutlet?: boolean;
  className?: string;
}

/**
 * UniversalTabs — route-aware horizontal tabs for record-scoped navigation.
 *
 * Each tab is a child route. The parent route should declare children that match
 * the `to` of each tab; the active tab is highlighted via NavLink's isActive.
 *
 * Use inside detail pages (e.g. CustomerDetail → Overview / Services / Invoices / Tickets).
 * For section-level navigation across sibling pages, use UniversalSubNav instead.
 */
export function UniversalTabs({
  items,
  actions,
  variant = 'underline',
  withOutlet = true,
  className,
}: UniversalTabsProps) {
  const styles = variant === 'pill' ? tabPatterns.pill : tabPatterns.horizontal;

  return (
    <div className={cn('space-y-6', className)}>
      <div
        className={cn(
          'flex items-center justify-between gap-4',
          variant === 'pill' && 'pb-0'
        )}
      >
        <nav className={styles.list} role="tablist" aria-label="Tabs">
          {items.map((item) => (
            <TabLink key={item.to} item={item} variant={variant} />
          ))}
        </nav>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>

      {withOutlet && (
        <div className={variant === 'underline' ? tabPatterns.horizontal.content : undefined}>
          <Outlet />
        </div>
      )}
    </div>
  );
}

function TabLink({
  item,
  variant,
}: {
  item: UniversalTabItem;
  variant: 'underline' | 'pill';
}) {
  const styles = variant === 'pill' ? tabPatterns.pill : tabPatterns.horizontal;
  const Icon = item.icon;

  if (item.disabled) {
    return (
      <span
        className={cn(styles.trigger, 'cursor-not-allowed opacity-50')}
        aria-disabled="true"
      >
        {Icon && <Icon className="h-4 w-4 shrink-0" />}
        {item.label}
        {item.badge !== undefined && (
          <span className="ml-1 rounded-pill bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
            {item.badge}
          </span>
        )}
      </span>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.end}
      role="tab"
      className={({ isActive }) =>
        cn(
          isActive ? styles.triggerActive : styles.trigger,
          'inline-flex items-center gap-2',
          focusUtils.ring
        )
      }
    >
      {Icon && <Icon className="h-4 w-4 shrink-0" />}
      <span>{item.label}</span>
      {item.badge !== undefined && (
        <span className="ml-1 rounded-pill bg-muted px-1.5 py-0.5 text-xs">
          {item.badge}
        </span>
      )}
    </NavLink>
  );
}
